import React from "react";
import {
	Nav,
	NavLink,
	NavbarContainer,
	Span,
	NavLogo,
	NavLogoMark,
	NavItems,
	NavItem,
	GitHubButton,
	ButtonContainer,
	MobileIcon,
	MobileMenu,
	MobileLink,
	MobileRouteLink,
	MobileGroupHeading
} from "./NavbarStyledComponent";
import { FaBars } from "react-icons/fa";
import { BIOGRAPHY } from "../../../constants/biography";
import { NAV_ABOUT, NAV_GROUPS } from "../../../constants/navigation";
import type { NavDestination, NavGroup } from "../../../constants/navigation";
import NavDropdown from "./NavDropdown";
import TopRibbon from "../topRibbon/TopRibbon";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import { SourishMukherjeeLogo } from "../../../assets";

/* Four imports were removed here in D-034 — `DiCssdeck`, `Close`,
   `CloseRounded` and `SourishMukherjeeBrandLogo`, none of them referenced.
   The last one mattered beyond tidiness: it pulled the shared components
   barrel into this module's graph, the same re-export-is-not-inert trap that
   D-018 and D-020 both turned on. `ThemeToggle` is imported by path for that
   reason, not through the barrel. */

/**
 * THE DESTINATION SET LIVES IN `constants/navigation.ts` (D-068), not here.
 *
 * This component held one copy of the list, the mobile menu below held a
 * second, and the Footer held a third. Ten destinations across three literals
 * is thirty strings that must be edited together — and the rule that governs
 * them (Constitution II.4: only mounted sections are listed, in page order)
 * can only be enforced in one place. The three call sites now read the same
 * export, so a destination cannot appear in one navigation and be missing from
 * another.
 *
 * TWO KINDS OF LINK, unchanged and still not interchangeable. Section anchors
 * are root-relative (`/#about`, not `#about`) so they work from every route: a
 * bare `#about` resolves against the current path and matches nothing on
 * /stories. Routes use a react-router <Link>, and they must, because
 * react-router does not scroll to a hash on client-side navigation — so a
 * <Link to="/#about"> would silently stop scrolling. Anchors stay <a>; routes
 * stay <Link>.
 */

const Navbar = () => {
	const [isOpen, setIsOpen] = React.useState(false);
	/**
	 * Which dropdown is open, held here rather than inside each dropdown, for
	 * one reason: only one may be open at a time, and that is a fact about the
	 * ROW, not about any group in it. Three independent booleans would let two
	 * panels overlap the moment a mouse crossed from one trigger to the next.
	 */
	const [openGroup, setOpenGroup] = React.useState<string | null>(null);
	const navItemsRef = React.useRef<HTMLUListElement | null>(null);

	const openDropdown = React.useCallback((id: string) => setOpenGroup(id), []);
	/**
	 * Closes ONLY if this group is still the open one. The mouse-leave grace
	 * timer fires ~140ms late, by which time the pointer may already have
	 * opened the next group — an unconditional close would shut the panel the
	 * visitor just asked for.
	 */
	const closeDropdown = React.useCallback(
		(id: string) => setOpenGroup((prev: string | null) => (prev === id ? null : prev)),
		[]
	);

	/**
	 * The two ways out that no element inside the row can see: a click landing
	 * anywhere else on the page, and Escape pressed while the menu was opened
	 * by hover, so focus is still on the document body. Both are only bound
	 * while something is actually open.
	 */
	React.useEffect(() => {
		if (openGroup === null) return undefined;

		const onPointerDown = (event: PointerEvent) => {
			const root = navItemsRef.current;
			if (root && event.target instanceof Node && root.contains(event.target)) return;
			setOpenGroup(null);
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpenGroup(null);
		};

		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [openGroup]);

	const closeMobile = () => setIsOpen(false);

	return (
		<>
			<TopRibbon />
			<Nav>
				<NavbarContainer>
					<NavLogo to="/">
						{/* A <span>, not an <a> — corrected in D-042, and it was a real
						    defect rather than a tidy-up. NavLogo is styled(Link), which
						    renders an <a>, so this inner element made a nested anchor:
						    invalid HTML that no parser will build, because the tokenizer
						    auto-closes the outer <a> the moment it meets the inner one.

						    It was harmless for as long as the site was a pure SPA —
						    React constructed the DOM directly and the markup never went
						    through the HTML parser. Static pre-rendering does round-trip
						    through it, so the parsed DOM stopped matching React's tree
						    and hydration failed the whole root, falling back to a full
						    client render: the exact "double render" the migration is
						    meant to eliminate. One invalid element, four hydration
						    errors, and an entire page re-rendered.

						    The element carried no href and no handler, so it was never a
						    link — only a flex wrapper. `display: flex` is preserved, so
						    a span lays out identically. */}
						<span
							style={{
								display: "flex",
								alignItems: "center",
								color: "white",
								cursor: "pointer"
							}}
						>
							<NavLogoMark src={SourishMukherjeeLogo} alt="Sourish Mukherjee Logo" />
							{/* `.text-gradient-wordmark`, not `.text-gradient` — this is 18px,
							    so the 4.5:1 floor applies rather than the large-text 3:1
							    (D-054). The hero h1 keeps `.text-gradient`.

							    AND NO NEGATIVE MARGIN (D-058). `marginLeft: "-1.7rem"` sat
							    here to close the monogram asset's transparent padding, and
							    since that padding is a FRACTION of the image while -27.2px
							    is not, it overshot at every width below 1280 — covering 78%
							    of the visible mark at 768. The image now carries its own
							    clamp and its own padding is the gap. */}
							<Span className="text-gradient-wordmark">{"Portfolio"}</Span>
						</span>
					</NavLogo>
					{/* The handler moved off the <svg> and onto the button in D-034;
					    an onClick on a bare icon is mouse-only.

					    This is the only navigation below 769px. D-059 had to widen that
					    band to 1025px because eight flat labels could not fit one row at
					    769 — the grouping in D-068 is what gives the tablet band its
					    real navigation back, and it is why this breakpoint moved down
					    again rather than the hamburger being restyled. */}
					<MobileIcon
						type="button"
						aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
						aria-expanded={isOpen}
						onClick={() => {
							setIsOpen(!isOpen);
						}}
					>
						<FaBars aria-hidden="true" />
					</MobileIcon>
					{/* FOUR ITEMS, NOT TEN. D-066 measured the ninth flat label wrapping
					    this row and D-067 measured the tenth joining the second row; no
					    gap, label or logo lever closed it, because the arithmetic was
					    never close. "About" plus three group triggers is a set that fits
					    at 769px, which is the whole reason the grouping exists. */}
					<NavItems ref={navItemsRef}>
						<NavItem>
							<NavLink href={NAV_ABOUT.href}>{NAV_ABOUT.label}</NavLink>
						</NavItem>
						{NAV_GROUPS.map((group: NavGroup) => (
							<NavDropdown
								key={group.id}
								group={group}
								isOpen={openGroup === group.id}
								onOpen={openDropdown}
								onClose={closeDropdown}
							/>
						))}
					</NavItems>
					<ButtonContainer>
						{/* Desktop placement. `ButtonContainer` is display:none at
						    <=768px (D-059 made it 1024, D-068 returns it), so the mobile
						    menu carries its own copy below — one toggle rendered twice,
						    never two mounted at once. */}
						<ThemeToggle />
						{/* "Connect", not "Connect me on LinkedIn" (D-059) — the single
						    biggest of the three D-038 levers. The long label measured
						    236px of a track that was 25px short of holding eight labels
						    on one row at 1280; this returns ~125px of it. `aria-label`
						    carries the full text, so the ACCESSIBLE NAME is unchanged and
						    the mobile menu's copy below can keep reading in full — the
						    two are never mounted at once. */}
						<GitHubButton href={BIOGRAPHY.linkedin} target="_blank" aria-label="Connect me on LinkedIn">
							Connect
						</GitHubButton>
					</ButtonContainer>
					{isOpen && (
						<MobileMenu isOpen={isOpen}>
							{/* THE GROUPING IS SHOWN, NOT RE-IMPLEMENTED (D-068). The phone
							    menu lists every destination at once under a heading per
							    group; it does not nest a second disclosure inside a menu
							    that is already a disclosure. Two taps to reach a link, on
							    the surface that is the ONLY navigation below 769px, to
							    save vertical space that the panel scrolls through anyway,
							    is a worse trade than the extra height. */}
							<MobileLink href={NAV_ABOUT.href} onClick={closeMobile}>
								{NAV_ABOUT.label}
							</MobileLink>
							{NAV_GROUPS.map((group: NavGroup) => (
								<React.Fragment key={group.id}>
									<MobileGroupHeading>{group.label}</MobileGroupHeading>
									{group.items.map((item: NavDestination) =>
										item.to ? (
											<MobileRouteLink key={item.label} to={item.to} onClick={closeMobile}>
												{item.label}
											</MobileRouteLink>
										) : (
											<MobileLink key={item.label} href={item.href} onClick={closeMobile}>
												{item.label}
											</MobileLink>
										)
									)}
								</React.Fragment>
							))}
							{/* Mobile placement of the toggle — see the desktop note above. */}
							<ThemeToggle />
							<GitHubButton
								style={{
									padding: "10px 16px",
									background: "var(--accent-interactive)",
									color: "var(--accent-interactive-contrast)",
									width: "max-content"
								}}
								href={BIOGRAPHY.linkedin}
								target="_blank"
							>
								Connect me on LinkedIn
							</GitHubButton>
						</MobileMenu>
					)}
				</NavbarContainer>
			</Nav>
		</>
	);
};

export default Navbar;
